/**
 * Relationship condition renderer.
 * Matches ~/code/ui condition-editor-v2/nodes/relationship/view.gts
 *
 * Renders: Relationship to **objectName** exists/does not exist
 * with optional split sub-filters for object vs relationship attributes.
 * The first filter list's lead-in ends the sentence; subsequent lists carry
 * their lead-in as the detail list's label (per Pluma's ConditionViewer docs).
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConditionViewerItem, ConditionViewerTerm } from '@customerio/pluma-components/react';
import { getObjectTypesQueryOptions } from '@/services/db/object-type/queries';
import type { RelationshipNode, ConditionFilter, TriggerRelationshipNode } from '../../types';
import { getIconForNode } from '../data-source-icon';
import { FieldOperatorValue, FilterDetailList, FiltersLeadIn, filtersLeadInText } from '../condition-parts';

function splitFilters(filters: ConditionFilter[] | undefined): {
  objectFilters: ConditionFilter[];
  relationshipFilters: ConditionFilter[];
} {
  if (filters == null || filters.length === 0) {
    return { objectFilters: [], relationshipFilters: [] };
  }

  const objectFilters: ConditionFilter[] = [];
  const relationshipFilters: ConditionFilter[] = [];

  for (const filter of filters) {
    if (filter.sourceType === 'object') {
      objectFilters.push(filter);
    } else {
      relationshipFilters.push(filter);
    }
  }

  return { objectFilters, relationshipFilters };
}

type RelationshipConditionProps = {
  node: RelationshipNode;
  workspaceId: number;
};

type NamedObjectType = Pick<RelationshipNode, 'objectTypeId' | 'objectTypeName'>;

/** The object type's name: the one the condition carries, or the workspace's, or its id while neither is known. */
function useObjectTypeName(node: NamedObjectType, workspaceId: number): string {
  const queryClient = useQueryClient();
  const needsLookup = node.objectTypeName == null && node.objectTypeId != null && workspaceId > 0;
  const { data } = useQuery({
    ...getObjectTypesQueryOptions(workspaceId, queryClient),
    enabled: needsLookup,
  });
  if (node.objectTypeName != null) {
    return node.objectTypeName;
  }
  const objectType = data?.find((candidate) => String(candidate.id) === String(node.objectTypeId));
  if (objectType != null) {
    return objectType.singular_name ?? objectType.name;
  }
  return node.objectTypeId != null ? `object #${node.objectTypeId}` : 'object';
}

/**
 * A batch update's `related` row, as Ember's legacy related label words it
 * with `useRelatedOverRelationship` (`cio-condition-label-item/related`):
 * `Are related to any Account object`.
 */
function relatedLead(node: RelationshipNode): string {
  const objectSource = node.relatedAs?.objectSource;
  const personSource = node.relatedAs?.personSource ?? '';
  let objects = 'any';
  if (objectSource === 'trigger') {
    objects = 'the trigger';
  } else if (personSource !== '') {
    objects = 'the same';
  }
  return `${node.inverse ? 'Are not related to' : 'Are related to'} ${objects}`;
}

function relatedTail(node: RelationshipNode): string {
  const personSource = node.relatedAs?.personSource ?? '';
  if (node.relatedAs?.objectSource === 'trigger' || personSource === '') {
    return 'object';
  }
  return `object as ${personSource === 'trigger' ? 'the trigger person' : 'the current person'}`;
}

export function RelationshipCondition({ node, workspaceId }: RelationshipConditionProps) {
  const name = useObjectTypeName(node, workspaceId);
  const { objectFilters, relationshipFilters } = splitFilters(node.filters);

  const lists: Array<{ filters: ConditionFilter[]; label: string }> = [];
  if (objectFilters.length > 0) lists.push({ filters: objectFilters, label: `${name} attributes` });
  if (relationshipFilters.length > 0) lists.push({ filters: relationshipFilters, label: 'relationship attributes' });
  const [firstList, ...restLists] = lists;

  return (
    <ConditionViewerItem
      icon={getIconForNode(node)}
      details={
        firstList == null ? undefined : (
          <>
            <FilterDetailList filters={firstList.filters} />
            {restLists.map((list) => (
              <FilterDetailList
                key={list.label}
                filters={list.filters}
                label={filtersLeadInText(node.filtersOperator, list.label)}
              />
            ))}
          </>
        )
      }
    >
      {node.relatedAs == null ? (
        <>
          Relationship to <ConditionViewerTerm>{name}</ConditionViewerTerm> {node.inverse ? 'does not exist' : 'exists'}
        </>
      ) : (
        <>
          {relatedLead(node)} <ConditionViewerTerm>{name}</ConditionViewerTerm> {relatedTail(node)}
        </>
      )}
      {firstList == null ? null : (
        <FiltersLeadIn filters={firstList.filters} filtersOperator={node.filtersOperator} label={firstList.label} />
      )}
    </ConditionViewerItem>
  );
}

type TriggerRelationshipConditionProps = {
  node: TriggerRelationshipNode;
  workspaceId: number;
};

/**
 * A comparison on the trigger relationship's attribute, as Ember's legacy
 * relationship label words it (`cio-condition-label-item/relationship`):
 * `The person added to the Account where their relationship role is equal to admin`.
 */
export function TriggerRelationshipCondition({ node, workspaceId }: TriggerRelationshipConditionProps) {
  const name = useObjectTypeName(node, workspaceId);
  return (
    <ConditionViewerItem icon={getIconForNode(node)}>
      {node.isRelationshipChange ? 'The person whose relationship to the' : 'The person added to the'}{' '}
      <ConditionViewerTerm>{name}</ConditionViewerTerm>
      {node.isRelationshipChange ? ' changed' : null} where their relationship{' '}
      <FieldOperatorValue
        field={node.field}
        operator={node.operator}
        valueSource={node.valueSource}
        value={node.value}
        value2={node.value2}
      />
    </ConditionViewerItem>
  );
}
